// @givmo/app-admin — where a finished SSO test connection lands (AS-WEB6).
//
// The backend's callback answers a 303 to the ceremony's `redirect_uri` carrying either
// `?sso_test=complete` or `?error=<CODE>`. This is that target: each host's own callback route on
// its own origin (`security-host.tsx` — the portal's `/settings/security/sso/test-callback`, the
// console's beside its Security page), registered per environment in
// `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`.
//
// ══ WHY IT IS A ROOT ROUTE AND NOT AN AUTHENTICATED ONE ═══════════════════════════════════════
// It renders OUTSIDE the `_authenticated` layout, which is deliberate and slightly surprising, so:
//
//   • It is usually a POP-UP whose only job is to close itself. Putting the auth guard in front of
//     it means a browser that dropped the session cookie in the pop-up context (or an Owner whose
//     token expired during a slow identity-provider sign-in) gets bounced to `/login-home` INSIDE a
//     window that is about to close — a sign-in screen flashing past on the way to nothing.
//   • There is nothing to protect. This component reads two values out of its own URL and renders
//     static text. It calls no endpoint, holds no company data, and the outcome it points at is
//     read on the settings page through the ordinary Owner-gated request.
//
// ══ ONE PATH, NOT TWO — CLOSE, THEN FALL BACK TO NAVIGATING ═══════════════════════════════════
// ⚠ THIS PAGE CANNOT TELL WHICH WINDOW IT IS IN, and an earlier draft branched as though it could.
// It dispatched on `window.opener`, calling `close()` when one was present and navigating otherwise
// — but the ceremony window is DISOWNED at open (`opener = null`, see `sso-section.tsx`), so
// `opener` is null in the pop-up too. Every pop-up therefore took the "this must be the Owner's own
// tab" branch and loaded the entire authenticated portal inside a window whose only job was to shut.
//
// So there is no dispatch. Both cases run the same two steps, and each covers the other's gap:
//
//   1. ATTEMPT `close()`. A script-opened window is script-closable even with its opener disowned,
//      so the ordinary pop-up ends here. The Owner's own tab is NOT script-closable (it has history
//      and nothing opened it), so the call is a no-op there rather than something to guard against.
//   2. ON A SHORT BEAT, IF STILL HERE, NAVIGATE to the host's Security surface. That is the whole
//      of the same-tab case (pop-up blocked, or a provider that forced a top-level navigation), and
//      it is also the fallback for a browser that refused step 1. Either way the tab that resumes
//      polling rehydrates the `test_id` from `sessionStorage`.
//
// ⚠ IT REPORTS NOTHING AND STORES NOTHING. There is no opener to message, and the outcome belongs to
// the Owner-gated `GET …/test-connection/{id}`, which is the only thing entitled to state it. An
// earlier draft stamped the outcome and the raw `?error=` code into `sessionStorage` — nothing read
// them back, and a stored verdict nobody consults is how a later session ends up rendering one this
// page never had the standing to give.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, GivmoWordmark, testIdProp } from '@givmo/design-system';
import { SSO_COPY } from './security-copy';
import { useSecurityHost } from './security-host';

export const SSO_TEST_CALLBACK_TEST_ID = 'sso-test-callback';

/**
 * How long to give `close()` before falling back to navigating.
 *
 * Short enough that the Owner's own tab does not sit on this page, long enough that an ordinary
 * pop-up is gone before the timer runs and never paints the return affordance on its way out.
 */
export const RETURN_FALLBACK_MS = 400;

export interface SsoTestCallbackPageProps {
  /** The `sso_test` query value — `'complete'` when the ceremony finished. */
  ssoTest?: string;
  /** The `error` query value — an `SSO_*` code when it did not. Never rendered to the Owner. */
  error?: string;
}

/**
 * Which outcome the URL describes.
 *
 * ★ ANYTHING THAT IS NOT AN EXPLICIT `complete` IS AN ERROR, including an empty query. A bare visit
 * to this route means a ceremony did not deliver its outcome, and the honest thing to say is that
 * the test did not finish — presenting a URL we cannot read as success would report a passing test
 * the server never reported.
 */
export function callbackOutcome(props: SsoTestCallbackPageProps): 'complete' | 'error' {
  return props.ssoTest === 'complete' ? 'complete' : 'error';
}

export function SsoTestCallbackPage({ ssoTest, error }: SsoTestCallbackPageProps) {
  const navigate = useNavigate();
  // Where this host's Security surface is (the portal's tab, the console's page — `security-host.tsx`).
  const { testReturn } = useSecurityHost();
  const outcome = callbackOutcome({ ssoTest, error });
  // Set on the beat, when this window is still alive — so the return affordance appears only for the
  // people who actually need it rather than flashing on every pop-up on its way out. It is the
  // last-resort visible route: the effect also navigates, and this is what remains if that does not
  // happen either.
  const [stillOpen, setStillOpen] = useState(false);

  useEffect(() => {
    // Step 1. Silently ignored for a window the script did not open, which is exactly the behaviour
    // the same-tab case needs — so this is unconditional rather than guarded.
    try {
      globalThis.close();
    } catch {
      // Refused outright. Step 2 covers it.
    }

    // Step 2. `close()` is asynchronous and gives no failure signal, so the fallback runs on a beat
    // rather than on the (absent) rejection. If this window is still alive, it is the Owner's own
    // tab (or a browser that refused to close a pop-up) and belongs back on the host's Security
    // surface — the portal's tab, the console's page.
    const timer = globalThis.setTimeout(() => {
      setStillOpen(true);
      void navigate({ to: testReturn.to, search: testReturn.search, replace: true });
    }, RETURN_FALLBACK_MS);
    return () => globalThis.clearTimeout(timer);
  }, [navigate, testReturn]);

  const failed = outcome === 'error';

  return (
    // The out-of-shell notice look both consoles' own such screens wear (the console's no-access and
    // console-unavailable notices): the sunken ground, the wordmark, a 22px heading, centred text
    // (D-AS-38 (1)).
    <main
      className="flex min-h-dvh flex-col items-center justify-center gap-16 bg-surface-sunken p-24 text-center"
      {...testIdProp(SSO_TEST_CALLBACK_TEST_ID)}
    >
      <GivmoWordmark className="h-[24px] w-auto text-brand-500" aria-label="Givmo" />
      <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
        {failed ? SSO_COPY.callbackErrorTitle : SSO_COPY.callbackTitle}
      </h1>
      <p className="max-w-[440px] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {failed ? SSO_COPY.callbackErrorBody : SSO_COPY.callbackBody}
      </p>
      {stillOpen && (
        <Button
          appearance="saas"
          tone="secondary"
          onClick={() =>
            void navigate({ to: testReturn.to, search: testReturn.search, replace: true })
          }
          testId={`${SSO_TEST_CALLBACK_TEST_ID}-return`}
        >
          {SSO_COPY.callbackReturn}
        </Button>
      )}
    </main>
  );
}
